<template>
  <div class="d-flex justify-content-end mt-2 align-items-center dashboard-filters">
    <div class="mr-2">
      <BaseFormSelect
        id="timezone"
        v-model="timeZoneLocalCopy"
        internal-platform-input
        :small="true"
        name="Timezone"
        data-cy="select-timezone"
        text-field="display"
        value-field="id"
        form-group-class="mb-0"
        :options="timeZonesList"
        horizontal
        width="9"
      />
    </div>

    <div>
      <v-datepicker
        v-model="dateRangeLocalCopy"
        format="MMM D, YYYY h:mm A"
        range-separator="  -  "
        type="datetime"
        range
        confirm
        icon-calendar=""
        input-class="mx-input custom-date-range-selector dashboard-filters__item custom-form-input--sm"
        data-cy="multiselect-daterange"
        confirm-text="Apply"
        :use12h="true"
        :shortcuts="shortcuts"
      >
        <template slot="icon-calendar">
          <div />
        </template>
        <template slot="icon-clear">
          <div />
        </template>
      </v-datepicker>
    </div>

    <div class="ml-2 dashboard-filters__item">
      <b-form-group>
        <b-button-group>
          <BaseButtonLoader
            size="sm"
            data-cy="button-auto-refresh"
            class="mb-0"
            button-class="dashboard-filters__item dashboad-filters__refresh-button"
            nowrap
            :width="3"
            @click="() => refresh()"
          >
            <font-awesome-icon
              class="mr-0"
              :icon="['fas', 'sync']"
            />
          </BaseButtonLoader>
        </b-button-group>
      </b-form-group>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { showErrorMessage } from '@/notification-utils'
import moment from 'moment-timezone'

export default {
  name: 'DashboardFilters',
  props: {
    toggleAutoRefresh: {
      type: Function,
      default: () => {}
    },
    autoRefresh: {
      type: Boolean,
      default: false
    },
    refresh: {
      type: Function,
      default: () => {}
    },

    dateRange: {
      type: Array,
      default() {
        return []
      }
    },

    timeZone: {
      type: String,
      default: ''
    },

    maxDateRangeInDays: {
      type: Number,
      default: 31
    }
  },
  data() {
    return {
      timezone: null,
    }
  },
  computed: {
    ...mapState('common-entities', [
      'timeZones'
    ]),

    shortcuts() {
      const getM = () => this.timeZone ? moment.tz(this.timeZone) : moment()
      return [
        {
          text: 'Today',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('day').toDate()
            const end = m.clone().endOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'Yesterday',
          onClick: () => {
            const m = getM().subtract(1, 'days')
            const start = m.clone().startOf('day').toDate()
            const end = m.clone().endOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last 7 Days',
          onClick: () => {
            const m = getM()
            const end = m.clone().endOf('day').toDate()
            const start = m.clone().subtract(6, 'days').startOf('day').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Week',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('week').toDate()
            const end = m.clone().endOf('week').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last Week',
          onClick: () => {
            const m = getM().subtract(1, 'week')
            const start = m.clone().startOf('week').toDate()
            const end = m.clone().endOf('week').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Month',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('month').toDate()
            const end = m.clone().endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last Month',
          onClick: () => {
            const m = getM().subtract(1, 'month')
            const start = m.clone().startOf('month').toDate()
            const end = m.clone().endOf('month').toDate()
            return [start, end]
          }
        },
        {
          text: 'This Year',
          onClick: () => {
            const m = getM()
            const start = m.clone().startOf('year').toDate()
            const end = m.clone().endOf('year').toDate()
            return [start, end]
          }
        },
        {
          text: 'Last 5 Years',
          onClick: () => {
            const m = getM()
            const end = m.clone().endOf('day').toDate()
            const start = m.clone().subtract(5, 'years').startOf('day').toDate()
            return [start, end]
          }
        }
      ]
    },

    dateRangeLocalCopy: {
      get() {
        return this.dateRange
      },

      set([from, to]) {
        this.$emit('date-range-updated', [from, to])
      }
    },

    timeZoneLocalCopy: {
      get() {
        return this.timeZone
      },
      set(value) {
        this.$emit('update-time-zone', value)
      }
    },

    autoRefreshButtonVariant() {
      return this.autoRefresh ? 'primary' : 'default'
    },
    
    timeZonesList() {
      const timeZonesList = [...this.timeZones]
      timeZonesList.unshift({ display: 'Please select', value: null })
      return timeZonesList
    },    
  },

  methods:{
    daysToMilliseconds(days) {
      return days * 24 * 60 * 60 * 1000
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard-filters {
  margin: 0 10px;
}

::v-deep .daterangepicker {
  .ranges {
    overflow-x: hidden;
    padding: 8px;
    min-width: 140px; /* Increased width to prevent scroll */
    
    li {
      margin: 2px 4px;
      padding: 8px 12px;
    }
  }

  .drp-buttons {
    padding: 12px;
  }
}
</style>
